import axios from "axios";
import API_BASE_URL from "./apiBase";

// Login token (JWT) from /api/v1/user/login. Every API request carries it;
// the backend rejects requests without a valid one.
export const TOKEN_KEY = "authToken";
export const SESSION_EXPIRED_KEY = "sessionExpired";

export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const urlOf = (input) => (typeof input === "string" ? input : input?.url || String(input || ""));
const isApiCall = (url) => url.startsWith(`${API_BASE_URL}/api/`) || url.startsWith("/api/");
const isLogin = (url) => url.includes("/api/v1/user/login");

// A 401 means the token is missing, expired or revoked: back to the login page.
let redirecting = false;
const sessionEnded = () => {
  if (redirecting || window.location.pathname === "/") return;
  redirecting = true;
  try {
    localStorage.clear();
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch {
    // storage unavailable; the redirect still logs them out
  }
  window.location.assign("/");
};

let installed = false;

// Adds `Authorization: Bearer <token>` to fetch and axios calls to our API
// (without touching headers a caller set itself), so the existing request
// code works unchanged.
export function installAuthInterceptors() {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = urlOf(input);
    if (!isApiCall(url)) return originalFetch(input, init);

    const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
    const token = getToken();
    const bearer = headers.get("Authorization");
    if (token && (!bearer || bearer === "Bearer ")) headers.set("Authorization", `Bearer ${token}`);

    const response = await originalFetch(input, { ...init, headers });
    if (response.status === 401 && !isLogin(url)) sessionEnded();
    return response;
  };

  axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token && isApiCall(urlOf(config.url))) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401 && !isLogin(urlOf(error.config?.url))) sessionEnded();
      return Promise.reject(error);
    }
  );
}
